import { Content, Main, SubContent } from "../../../components/Layout";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import useWrapper from "../../../hooks/useWrapper";
import { traineeFinancial } from "../../../hooks/api/listFinancial";
import { toast } from "react-toastify";
import { Row, Col } from "react-bootstrap";
import TraineeSubMenu from "../../../components/Submenu/TraineeSubMenu";
import Loading from "../../../components/Loading";
import SlipsTable from "../../../components/SlipsTable";
import Results from "../../../components/Results";
import StatusFlag from "../../../components/StatusFlag";
import InfoModal from "../../../components/InfoModal";
import EmptyError from "../../../components/EmptyError";
import Button from "../../../components/Button";
import ModalDistratoWaive from "../../../components/Modal/modal-distrato-waive";
import TraineePendingDistrato from "../../../components/TraineePendingDistrato";
import usePermission from "../../../hooks/usePermission";
import useFormat from "../../../hooks/utils/useFormat";
import api from "../../../services/api";

const TOLERANCE = 0.01;

/**
 * Aba dedicada ao Distrato do formando: o resumo do encerramento (multa, aporte,
 * já pago) e as parcelas da multa. Fica separada de "Cobranças Extras" para não
 * misturar a multa do distrato com as cobranças do dia a dia — o distrato é
 * registrado no Financeiro Contratado, mas vive aqui depois de criado.
 */
export default function TraineesDistrato() {
  const { me, config } = useWrapper();
  const { trainee, setisWaitingResponse, reloadTraineeData } = useOutletContext() || {};
  const { formatReal, formatDate } = useFormat();
  const canDistrato = usePermission("trainee", "distrato");
  const [showModalWaive, setshowModalWaive] = useState();

  const [reloadFinancial, setreloadFinancial] = useState();
  const reloadFinancialData = () => {
    setreloadFinancial(JSON.stringify(new Date()));
  };

  const [financial, setfinancial] = useState();
  const [distrato, setdistrato] = useState();
  const [isLoading, setisLoading] = useState();

  useEffect(() => {
    (async () => {
      try {
        setisLoading(true);
        const [_financial, _distratos] = await Promise.all([
          traineeFinancial({
            config,
            me,
            trainee_id: trainee?.id,
            commercial_id: trainee?.commercial_id,
          }),
          api.get(
            `distratos?trainee_id=${trainee?.id}&commercial_id=${trainee?.commercial_id}&paginate=false`,
            config,
          ),
        ]);
        setfinancial(_financial);
        // o mais recente primeiro (scopeFilter ordena por distrato_date desc)
        setdistrato(_distratos?.data?.[0]);
      } catch (__error) {
        console.log("__error", __error);
        toast.error("Problema ao carregar o distrato.");
      } finally {
        setisLoading(false);
      }
    })();
    // mesma convenção das outras abas: só refaz o fetch quando pedido explicitamente
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadFinancial]);

  useEffect(() => {
    setisWaitingResponse(isLoading);
  }, [isLoading, setisWaitingResponse]);

  const updateSlip = props => {
    const { slip_id, update } = props;
    setfinancial({
      ...financial,
      payment_slips: {
        ...financial?.payment_slips,
        distrato: financial?.payment_slips?.distrato?.map(__slip =>
          __slip.id === slip_id ? { ...__slip, ...update } : __slip,
        ),
      },
    });
  };

  const _slips = financial?.payment_slips?.distrato || [];
  const _active_slips = _slips.filter(__slip => __slip.status !== "Cancelado" && __slip.status !== "Pago");
  const _paid_slips = _slips.filter(__slip => __slip.status === "Pago");

  const _multa = distrato?.multa_value || 0;
  const _already_paid = distrato?.already_paid_total || 0;
  const _diff = _multa - _already_paid;
  const _is_charge = _diff > TOLERANCE;
  const _is_refund = _diff < -TOLERANCE;

  // quanto o formando já pagou nas parcelas da multa (sem juros por atraso, mesma
  // regra do backend). Prévia para o modal — o valor oficial vem da resposta dele.
  const _paid_on_slips = _paid_slips.reduce(
    (__acc, __slip) => __acc + ((__slip.value_paid || 0) - (__slip.value_juros || 0)),
    0,
  );
  const _is_waived = distrato?.status === "desistido";
  const _is_settled = distrato?.status === "quitado";
  const _can_waive = _is_charge && distrato?.status === "pendente";

  return (
    <Row>
      <Col sm={9}>
        <Main>
          {isLoading ? (
            <Content>
              <Loading />
            </Content>
          ) : !distrato ? (
            trainee?.bond_status === "encerrado_aguardando_distrato" ? (
              <TraineePendingDistrato
                trainee={trainee}
                reloadTraineeData={reloadTraineeData}
                onCompleted={reloadFinancialData}
              />
            ) : (
              <Content>
                <EmptyError msg="Nenhum Distrato Registrado para Este Formando" />
              </Content>
            )
          ) : (
            <Content>
              <Row>
                <Col>
                  <h2 className="mini-title">
                    Distrato: <span className="small-label">(Encerramento de Vínculo)</span>
                  </h2>
                  <p className="small-label mb-2">
                    Registrado em {formatDate(distrato?.distrato_date, 2)}
                    {!!distrato?.description && ` — ${distrato.description}`}
                  </p>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  {/* "Acesso Limitado" já é laranja no StatusFlag e é o mesmo rótulo
                      usado no badge de acesso do formando — mesma leitura nas duas telas */}
                  <StatusFlag status={_is_settled || _is_waived ? "Quitado" : "Acesso Limitado"}>
                    {_is_settled
                      ? `Distrato Quitado${distrato?.settled_at ? ` em ${formatDate(distrato.settled_at, 2)}` : ""}`
                      : _is_waived
                        ? `Cobrança Desistida${distrato?.waived_at ? ` em ${formatDate(distrato.waived_at, 2)}` : ""}`
                        : "Multa Pendente — Acesso Limitado"}
                  </StatusFlag>
                  <p className="small-label mt-2 mb-0">
                    {_is_settled
                      ? `O vínculo do formando está encerrado e o acesso, revogado.${
                          distrato?.aporte_value > 0 ? " O aporte já foi lançado para a turma." : ""
                        }`
                      : _is_waived
                        ? `A empresa desistiu de cobrar o saldo da multa (via judicial). O aporte já realizado foi transferido para a turma e ${
                            _active_slips.length > 0
                              ? "as parcelas em aberto seguem ativas — se o formando pagar, o valor entra como receita da empresa e nada mais vai para a turma."
                              : "novos pagamentos não geram mais aporte."
                          }`
                        : `Todos os pagamentos em aberto foram cancelados no registro do distrato. O acesso do formando fica limitado a Financeiro e Atendimento até a multa ser 100% quitada — só então o vínculo é encerrado${
                            distrato?.aporte_value > 0 ? " e o aporte é lançado para a turma" : ""
                          }.`}
                  </p>
                </Col>
                <Col lg={6}>
                  <Results
                    rows={[
                      { label: "Valor da Multa", value: formatReal(_multa) },
                      {
                        label: (
                          <>
                            Já Pago pelo Formando{" "}
                            <InfoModal
                              title="Já Pago pelo Formando"
                              body="Total apurado no momento do distrato. Não inclui juros/multa por atraso pagos pelo formando — apenas o valor das parcelas."
                            />
                          </>
                        ),
                        value: formatReal(_already_paid),
                      },
                      { label: "Aporte para a Turma", value: formatReal(distrato?.aporte_value || 0) },
                      ...(distrato?.aporte_transferred_value > 0
                        ? [
                            {
                              label: "Aporte Já Transferido",
                              value: formatReal(distrato.aporte_transferred_value),
                            },
                          ]
                        : []),
                      _is_charge
                        ? { label: <b>Valor Cobrado (Multa)</b>, value: <b>{formatReal(_diff)}</b>, type: "dark" }
                        : _is_refund
                          ? {
                              label: <b>Reembolso ao Formando</b>,
                              value: <b>{formatReal(Math.abs(_diff))}</b>,
                              type: "dark",
                            }
                          : {
                              label: <b>Situação</b>,
                              value: <b>Sem Movimentação Financeira</b>,
                              type: "dark",
                            },
                    ]}
                  />
                </Col>
              </Row>

              {_is_charge ? (
                <>
                  <Row className="mt-4">
                    <Col>
                      <h2 className="mini-title">
                        {_active_slips.length} Parcela{_active_slips.length === 1 ? "" : "s"} da Multa em Aberto:
                      </h2>
                      <p className="small-label mb-2">
                        {_is_waived
                          ? "A cobrança do saldo foi encerrada: estas parcelas seguem ativas, mas o que for pago aqui não gera mais aporte para a turma."
                          : "Boletos com vencimento em finais de semana e feriados poderão ser pagos no próximo dia útil sem multas e taxas. O acesso do formando é encerrado quando a última parcela for quitada."}
                      </p>
                    </Col>
                  </Row>
                  <SlipsTable
                    slips={_active_slips}
                    updateSlip={__update => {
                      updateSlip({ slip_id: __update.slip_id, update: __update.update });
                    }}
                    show_total={true}
                    reload={reloadFinancialData}
                    aporte_share={_is_waived ? 0 : distrato?.aporte_share}
                  />
                  {_paid_slips.length > 0 && (
                    <>
                      <Row className="mt-4">
                        <Col>
                          <h2 className="mini-title">
                            {_paid_slips.length} Parcela{_paid_slips.length === 1 ? "" : "s"} Paga
                            {_paid_slips.length === 1 ? "" : "s"}:
                          </h2>
                          <p className="small-label mb-2">Histórico dos pagamentos já efetuados sobre a multa.</p>
                        </Col>
                      </Row>
                      <SlipsTable
                        slips={_paid_slips}
                        paid_values={true}
                        show_total={true}
                        aporte_share={distrato?.aporte_share}
                      />
                    </>
                  )}
                </>
              ) : (
                <Row className="mt-4">
                  <Col>
                    <h2 className="mini-title">Parcelas da Multa:</h2>
                    <p className="small-label mb-2">
                      {_is_refund
                        ? "Não há cobrança: o formando já havia pago mais que a multa. O reembolso é um Título de Pagamento e aparece em “Reembolsos ao Formando”, no Financeiro Contratado, com a baixa feita em Contas a Pagar."
                        : "Não há cobrança nem reembolso: o valor já pago pelo formando equivale exatamente à multa."}
                    </p>
                  </Col>
                </Row>
              )}
            </Content>
          )}
        </Main>
      </Col>
      <Col sm={3}>
        <TraineeSubMenu trainee={trainee} />
        {/* mesma convenção de ação da barra lateral do Financeiro Contratado
            ("Encerrar Vínculo:"): título, explicação, divisor e botão vertical */}
        {_can_waive && canDistrato && (
          <SubContent className="mt-3">
            <h2 className="mini-title">Desistir da Cobrança:</h2>
            <p className="small-label mb-2 mt-1">
              Use quando o formando pagou parte da multa e a cobrança do saldo foi para a via judicial: repassa à turma
              o aporte já realizado e encerra a geração de aporte, sem cancelar os boletos.
            </p>
            <div className="divisor mt-3 mb-3"></div>
            <Button color="error" onClick={() => setshowModalWaive(true)} data-vertical="true">
              Transferir Aporte &amp; Desistir da Cobrança
            </Button>
          </SubContent>
        )}
      </Col>
      <ModalDistratoWaive
        show={!!showModalWaive}
        onHide={() => setshowModalWaive()}
        distrato={distrato}
        paidOnSlips={_paid_on_slips}
        refreshCallback={reloadFinancialData}
      />
    </Row>
  );
}
